import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Group,
  CalendarToday,
  Pets,
  CheckCircle,
  Info,
  Edit,
  NavigateBefore,
  NavigateNext,
  LocationOn,
  Male,
  Female,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";
import { useDashboard } from "../../../contexts/DashboardContext";
import { apiClientBff } from "../../../services/apiBff";

export interface CattleTag {
  name: string;
  tag: string;
  identifier: string;
}

export interface LotData {
  _key?: string;
  _id?: string;
  _rev?: string;
  farm_id?: string;
  name?: string;
  description?: string | null;
  cattle_tags?: CattleTag[];
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  current_paddock_id?: string;
}

export interface LotDetailsData {
  data: LotData;
  resumo?: string;
  url?: string;
}

export interface LotDetailsViewProps {
  lotData: LotDetailsData;
  onEdit?: () => void;
  onViewAnimals?: (tags: CattleTag[]) => void;
}

interface AnimalDetails {
  _key?: string;
  _id?: string;
  _rev?: string;
  farm_id?: string;
  current_farm_id?: string;
  name?: string;
  cattle_id?: {
    tag?: string;
    country_code?: string;
    manufacturer_code?: number;
    unique_id?: string;
  };
  breed_code?: string;
  gender?: string;
  birth_date?: string;
  birth_weight_kg?: number;
  current_paddock_id?: string;
  current_status?: string;
  created_at?: string;
  updated_at?: string;
  handling_history?: any[];
}

const formatDate = (dateString?: string, t?: any): string => {
  if (!dateString) return t ? t("common.not_informed") : "Não informado";
  try {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return t ? t("common.invalid_date") : "Data inválida";
  }
};

const getStatusColor = (isActive?: boolean) => {
  return isActive
    ? DESIGN_TOKENS.colors.brand.primary
    : DESIGN_TOKENS.colors.status.error[500];
};

export const LotDetailsView: React.FC<LotDetailsViewProps> = ({
  lotData,
  onEdit,
  onViewAnimals,
}) => {
  const { t } = useTranslation(["farm", "animal", "common"]);
  const { restorePreviousContent, updateContent } = useDashboard();
  
  const { data, resumo } = lotData || {};
  const [lotAnimals, setLotAnimals] = useState<AnimalDetails[]>([]);
  const [isLoadingAnimals, setIsLoadingAnimals] = useState<boolean>(false);
  const totalCount = lotAnimals.length;
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnimals = useMemo(() => {
    return lotAnimals.slice(startIndex, endIndex);
  }, [lotAnimals, startIndex, endIndex]);

  const formatBreed = (breedCode: string | undefined, tt: any): string => {
    if (!breedCode) return tt("animal:not_defined");
    return breedCode.charAt(0).toUpperCase() + breedCode.slice(1).toLowerCase();
  };

  const statusChipColor = (
    status?: string
  ): "default" | "success" | "warning" | "error" => {
    if (!status) return "default";
    const s = status.toLowerCase();
    if (
      s.includes("active") ||
      s.includes("ativo") ||
      s.includes("saud") ||
      s.includes("ok")
    )
      return "success";
    if (s.includes("pend") || s.includes("aten") || s.includes("warning"))
      return "warning";
    if (
      s.includes("erro") ||
      s.includes("doent") ||
      s.includes("inactive") ||
      s.includes("inativo")
    )
      return "error";
    return "default";
  };

  const getStatusLabel = (status?: string) => {
    if (!status) return "";
    const key = `animal:status_values.${status.toLowerCase()}`;
    const translated = t(key);
    return translated !== key ? translated : status;
  };

  const getGenderIcon = (gender?: string) => {
    if (!gender) return null;
    const g = gender.toLowerCase();
    if (g.includes("macho") || g.includes("male")) {
      return <Male sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.primary[600] }} />;
    }
    if (g.includes("fêmea") || g.includes("femea") || g.includes("female")) {
      return <Female sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.secondary[600] }} />;
    }
    return null;
  };

  const calculateAge = (birthDate: string | undefined, tt: any): string => {
    if (!birthDate) return tt("animal:not_defined");
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      let months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
      if (today.getDate() < birth.getDate()) {
        months--;
      }
      if (months < 0) months = 0;
      return months === 1
        ? tt("animal:time_units.months_one", { count: months })
        : tt("animal:time_units.months_other", { count: months });
    } catch {
      return tt("animal:not_defined");
    }
  };

  const openPassport = (animal: AnimalDetails) => {
    updateContent("animalPassport", {
      animalData: animal,
      previousContentView: "lotDetails",
    });
  };

  useEffect(() => {
    const fetchAnimals = async () => {
      if (!data?.cattle_tags || data.cattle_tags.length === 0) {
        setLotAnimals([]);
        return;
      }
      setIsLoadingAnimals(true);
      try {
        const names = data.cattle_tags.map((ct) => ct.name).filter(Boolean);
        const requestPayload = {
          bind_vars: {
            "@collection": "Cattle",
            "@collectionHandling": "Handling",
            cattle_tags: names,
            farm_id: data?.farm_id,
          },
          query:
            "FOR t IN @cattle_tags LET doc = FIRST(FOR c IN @@collection FILTER LOWER(c.name) == LOWER(t) AND c.farm_id == @farm_id RETURN c) FILTER doc != null LET handling_history = (FOR h IN @@collectionHandling FILTER LOWER(h.cattle_tag) == LOWER(t) AND h.farm_id == @farm_id RETURN h) RETURN MERGE(doc, { handling_history })",
        };
        const response: any = await apiClientBff.post("/cattle/search", requestPayload);
        const results = Array.isArray(response?.data) ? response.data : [];
        setLotAnimals(results);
      } catch (e) {
        setLotAnimals([]);
      } finally {
        setIsLoadingAnimals(false);
      }
    };
    fetchAnimals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.farm_id, data?.cattle_tags]);

  // Safety check to prevent null reference errors
  if (!data) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          {t("errors.data_not_found", { ns: "common" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: DESIGN_TOKENS.colors.organization.farm,
            mx: "auto",
            mb: 2,
            fontSize: "2rem",
          }}
        >
          <Box
            component="img"
            src="/gestores/lote-branco.svg"
            alt="Lote"
            sx={{ width: 48, height: 48 }}
          />
        </Avatar>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: DESIGN_TOKENS.colors.organization.farm,
            mb: 1,
          }}
        >
          {data.name || t("labels.unnamed_lot", { ns: "farm" })}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 2 }}
        >
          {resumo || t("labels.details_description", { ns: "farm" })}
        </Typography>
        <Chip
          icon={data.is_active ? <CheckCircle /> : <Info />}
          label={
            data.is_active
              ? t("status.active", { ns: "common" })
              : t("status.inactive", { ns: "common" })
          }
          sx={{
            bgcolor: getStatusColor(data.is_active),
            color: "white",
            fontWeight: 600,
            "& .MuiChip-icon": {
              color: "white",
            },
          }}
        />
      </Box>

      {/* Basic Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: DESIGN_TOKENS.colors.organization.farm,
            }}
          >
            {t("lot.basic_information", { ns: "farm" })}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <Typography
                variant="body2"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}
              >
                {t("labels.name", { ns: "farm" })}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {data.name || t("not_informed", { ns: "common" })}
              </Typography>
            </Box>

            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <Typography
                variant="body2"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}
              >
                {t("labels.id", { ns: "farm" })}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontFamily: "monospace", fontWeight: 600 }}
              >
                {data._key || t("not_informed", { ns: "common" })}
              </Typography>
            </Box>

            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <Typography
                variant="body2"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}
              >
                {t("labels.farm_name", { ns: "farm" })}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {data.farm_id || t("not_informed", { ns: "common" })}
              </Typography>
            </Box>

            {data.current_paddock_id && (
              <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
                <Typography
                  variant="body2"
                  sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}
                >
                  {t("labels.current_paddock", { ns: "farm" })}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {data.current_paddock_id}
                </Typography>
              </Box>
            )}
          </Box>

          {data.description && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}
              >
                {t("description", { ns: "common" })}
              </Typography>
              <Typography variant="body1">{data.description}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Animals in Lot */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="img"
                src="/vaca.png"
                alt="Vaca"
                sx={{ width: 24, height: 24 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("labels.animals_in_lot", { ns: "farm" })}
              </Typography>
            </Box>
            <Chip
              label={t("labels.animals_count", {
                ns: "farm",
                count: data.cattle_tags?.length || 0,
              })}
              sx={{
                bgcolor: DESIGN_TOKENS.colors.primary[100],
                color: DESIGN_TOKENS.colors.primary[700],
                fontWeight: 600,
              }}
            />
          </Box>

                {data.cattle_tags && data.cattle_tags.length > 0 ? (
                  <>
            {isLoadingAnimals ? (
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                {t("common:loading")}
              </Typography>
            ) : lotAnimals.length === 0 ? (
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                {t("labels.no_animals_associated", { ns: "farm" })}
              </Typography>
            ) : (
              <Box>
                <TableContainer component={Paper} elevation={1}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: DESIGN_TOKENS.colors.neutral[100] }}>
                        <TableCell sx={{ fontWeight: 600 }}>{t("animal:table_headers.animal")}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t("animal:table_headers.breed")}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t("animal:table_headers.gender")}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t("animal:table_headers.age")}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t("animal:table_headers.weight")}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t("animal:table_headers.location")}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t("animal:table_headers.status")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentAnimals.map((animal, index) => (
                        <TableRow
                          key={animal._key || animal._id || index}
                          sx={{ '&:hover': { backgroundColor: DESIGN_TOKENS.colors.neutral[50], cursor: 'pointer' } }}
                          onClick={() => openPassport(animal)}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: DESIGN_TOKENS.colors.organization.farm,
                                  fontSize: "0.875rem",
                                }}
                              >
                                {animal.name?.charAt(0) || "A"}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {animal.name || t("animal:no_name")}
                                </Typography>
                                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                                  {t("common:labels.id")}: {animal._key || "N/A"}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatBreed(animal.breed_code, t)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {getGenderIcon(animal.gender)}
                              <Typography variant="body2">
                                {animal.gender || t("animal:not_defined")}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {calculateAge(animal.birth_date, t)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {animal.birth_weight_kg ? `${animal.birth_weight_kg} ${t("common:units.kg")}` : t("animal:not_defined")}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <LocationOn sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                              <Typography variant="body2">
                                {animal.current_paddock_id || t("animal:not_defined")}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={getStatusLabel(animal.current_status) || t("animal:not_defined")}
                              size="small"
                              color={statusChipColor(animal.current_status)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalCount > itemsPerPage && (
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                        <Button
                          variant="outlined"
                          startIcon={<NavigateBefore />}
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          size="small"
                        >
                          {t('animal:previous')}
                        </Button>

                        <Stack direction="row" spacing={1}>
                          <Button
                            variant={currentPage === 1 ? "contained" : "outlined"}
                            onClick={() => setCurrentPage(1)}
                            size="small"
                            sx={{
                              minWidth: 40,
                              bgcolor: currentPage === 1 ? DESIGN_TOKENS.colors.organization.farm : 'transparent',
                              '&:hover': {
                                bgcolor: currentPage === 1 
                                  ? DESIGN_TOKENS.colors.organization.farm 
                                  : DESIGN_TOKENS.colors.neutral[100]
                              }
                            }}
                          >
                            1
                          </Button>

                          {currentPage > 3 && (
                            <Typography variant="body2" sx={{ alignSelf: 'center', px: 1 }}>...</Typography>
                          )}

                          {Array.from({ length: Math.min(4, totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (currentPage <= 3) {
                              pageNum = i + 2;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 1 + i;
                            }
                            if (pageNum <= 1 || pageNum >= totalPages || pageNum > totalPages) return null;
                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "contained" : "outlined"}
                                onClick={() => setCurrentPage(pageNum)}
                                size="small"
                                sx={{
                                  minWidth: 40,
                                  bgcolor: currentPage === pageNum ? DESIGN_TOKENS.colors.organization.farm : 'transparent',
                                  '&:hover': {
                                    bgcolor: currentPage === pageNum 
                                      ? DESIGN_TOKENS.colors.organization.farm 
                                      : DESIGN_TOKENS.colors.neutral[100]
                                  }
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}

                          {currentPage < totalPages - 2 && totalPages > 5 && (
                            <Typography variant="body2" sx={{ alignSelf: 'center', px: 1 }}>...</Typography>
                          )}

                          {totalPages > 1 && (
                            <Button
                              variant={currentPage === totalPages ? "contained" : "outlined"}
                              onClick={() => setCurrentPage(totalPages)}
                              size="small"
                              sx={{
                                minWidth: 40,
                                bgcolor: currentPage === totalPages ? DESIGN_TOKENS.colors.organization.farm : 'transparent',
                                '&:hover': {
                                  bgcolor: currentPage === totalPages 
                                    ? DESIGN_TOKENS.colors.organization.farm 
                                    : DESIGN_TOKENS.colors.neutral[100]
                                }
                              }}
                            >
                              {totalPages}
                            </Button>
                          )}
                        </Stack>

                        <Button
                          variant="outlined"
                          endIcon={<NavigateNext />}
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          size="small"
                        >
                          {t('animal:next')}
                        </Button>
                      </Stack>

                      <Typography
                        variant="caption"
                        sx={{ 
                          display: 'block', 
                          textAlign: 'center', 
                          mt: 2, 
                          color: DESIGN_TOKENS.colors.text.secondary 
                        }}
                      >
                        {itemsPerPage} {t('animal:items_per_page')}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
                  </>
                ) : (
            <Typography
              variant="body2"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                fontStyle: "italic",
              }}
            >
              {t("labels.no_animals_associated", { ns: "farm" })}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: DESIGN_TOKENS.colors.organization.farm,
            }}
          >
            {t("labels.timeline", { ns: "farm" })}
          </Typography>

          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CalendarToday
                sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
              />
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                >
                  {t("created_at", { ns: "common" })}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDate(data.created_at, t)}
                </Typography>
              </Box>
            </Box>

            {data.updated_at && data.updated_at !== data.created_at && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Edit sx={{ color: DESIGN_TOKENS.colors.text.secondary }} />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                  >
                    {t("last_updated", { ns: "common" })}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatDate(data.updated_at, t)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>



      {/* Action Button - Removido conforme solicitado */}
    </Box>
  );
};

export default LotDetailsView;
